import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Stack,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Score as ScoreIcon,
  TrendingUp,
  TrendingDown,
  EmojiEvents,
  Search,
  FilterList,
  Refresh,
  CheckCircle,
  Pending,
  SportsTennis,
} from '@mui/icons-material';
import { GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { DataTable } from '../../components/admin/DataTable';
import { ConfirmDialog } from '../../components/admin/ConfirmDialog';
import apiService from '../../services/api';
import { Match, Score } from '../../types';
import {
  EMPTY_SCORE_SETS,
  applySetChange,
  buildScoreSubmitPayload,
  countSetsWon,
  setsFromStored,
  syncSetRows,
} from '../../utils/scoreForm';

// Extended Score interface for admin page
interface AdminScore extends Score {
  team1_score?: number;
  team2_score?: number;
  is_confirmed?: boolean;
  submitted_by?: number;
  submitted_at?: string;
  confirmed_by?: number;
  confirmed_at?: string;
}

interface ScoreStats {
  total_scores: number;
  confirmed_scores: number;
  pending_scores: number;
  average_score: number;
}

export const AdminScoresPage: React.FC = () => {
  const [scores, setScores] = useState<AdminScore[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [stats, setStats] = useState<ScoreStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [editingScore, setEditingScore] = useState<AdminScore | null>(null);
  const [deletingScoreId, setDeletingScoreId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterMatchId, setFilterMatchId] = useState<string>('');
  
  const [formData, setFormData] = useState({
    match_id: '',
    sets: [...EMPTY_SCORE_SETS],
    team1_score: 0,
    team2_score: 0,
  });

  useEffect(() => {
    fetchScores();
    fetchMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    calculateStats();
  }, [scores]);

  const fetchScores = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAllScores();
      const data = Array.isArray(response.data) ? response.data : [];
      // Map API response to AdminScore format
      const mappedScores: AdminScore[] = data.map((score: any) => ({
        id: score.id,
        match_id: score.match_id,
        team1_score: score.team1_score,
        team2_score: score.team2_score,
        sets: score.sets || [],
        winner_team: score.winner_team,
        is_confirmed: score.is_confirmed,
        submitted_by: score.submitted_by,
        submitted_at: score.submitted_at,
        confirmed_by: score.confirmed_by,
        confirmed_at: score.confirmed_at,
        created_at: score.submitted_at || new Date().toISOString(),
        confirmed: score.is_confirmed || false,
      }));
      setScores(mappedScores);
    } catch (error: any) {
      console.error('Error fetching scores:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Failed to fetch scores', 
        severity: 'error' 
      });
      setScores([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatches = async () => {
    try {
      const response = await apiService.getMatches();
      const data = Array.isArray(response.data) ? response.data : [];
      setMatches(data);
    } catch (error) {
      console.error('Error fetching matches:', error);
    }
  };

  const calculateStats = () => {
    if (scores.length === 0) {
      setStats({
        total_scores: 0,
        confirmed_scores: 0,
        pending_scores: 0,
        average_score: 0,
      });
      return;
    }

    const confirmed = scores.filter(s => s.is_confirmed).length;
    const pending = scores.filter(s => !s.is_confirmed).length;
    
    const allScores: number[] = [];
    scores.forEach(s => {
      if (s.sets && s.sets.length > 0) {
        s.sets.forEach(set => {
          allScores.push(set.team1 || 0);
          allScores.push(set.team2 || 0);
        });
      } else {
        allScores.push(s.team1_score || 0);
        allScores.push(s.team2_score || 0);
      }
    });
    
    const average = allScores.length > 0 
      ? Math.round(allScores.reduce((acc, val) => acc + val, 0) / allScores.length) 
      : 0;

    setStats({
      total_scores: scores.length,
      confirmed_scores: confirmed,
      pending_scores: pending,
      average_score: average,
    });
  };

  const handleCreateScore = () => {
    setEditingScore(null);
    setFormData({
      match_id: '',
      sets: [...EMPTY_SCORE_SETS],
      team1_score: 0,
      team2_score: 0,
    });
    setOpenDialog(true);
  };

  const handleEditScore = (score: AdminScore) => {
    setEditingScore(score);
    const sets = setsFromStored(score.sets);
    const setsWon = countSetsWon(sets);

    setFormData({
      match_id: score.match_id?.toString() || '',
      sets,
      team1_score: setsWon.team1,
      team2_score: setsWon.team2,
    });
    setOpenDialog(true);
  };

  const handleSaveScore = async () => {
    try {
      if (!formData.match_id) {
        setSnackbar({ 
          open: true, 
          message: 'Match ID is required', 
          severity: 'error' 
        });
        return;
      }

      const scoreData = buildScoreSubmitPayload(formData.sets);

      if (editingScore) {
        await apiService.adminUpdateScore(editingScore.id, scoreData);
        setSnackbar({ 
          open: true, 
          message: 'Score updated successfully', 
          severity: 'success' 
        });
      } else {
        await apiService.adminSubmitScore(parseInt(formData.match_id), scoreData);
        setSnackbar({ 
          open: true, 
          message: 'Score created successfully', 
          severity: 'success' 
        });
      }
      setOpenDialog(false);
      fetchScores();
    } catch (error: any) {
      console.error('Error saving score:', error);
      setSnackbar({ 
        open: true, 
        message: error.message || error.response?.data?.detail || 'Failed to save score', 
        severity: 'error' 
      });
    }
  };

  const handleDeleteScore = (scoreId: number) => {
    setDeletingScoreId(scoreId);
    setOpenConfirmDialog(true);
  };

  const confirmDelete = async () => {
    if (deletingScoreId) {
      try {
        await apiService.deleteScore(deletingScoreId);
        setSnackbar({ open: true, message: 'Score deleted successfully', severity: 'success' });
        fetchScores();
      } catch (error: any) {
        console.error('Error deleting score:', error);
        setSnackbar({ 
          open: true, 
          message: error.response?.data?.detail || 'Failed to delete score', 
          severity: 'error' 
        });
      }
    }
    setOpenConfirmDialog(false);
    setDeletingScoreId(null);
  };

  const handleSetChange = (setIndex: number, field: 'team1' | 'team2', value: number) => {
    const nextSets = applySetChange(formData.sets, setIndex, field, value);
    const setsWon = countSetsWon(nextSets);
    setFormData({
      ...formData,
      sets: nextSets,
      team1_score: setsWon.team1,
      team2_score: setsWon.team2,
    });
  };

  // Filter scores
  const filteredScores = scores.filter(score => {
    const matchesSearch = !searchQuery || 
      score.match_id.toString().includes(searchQuery) ||
      score.id.toString().includes(searchQuery);
    
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'confirmed' && score.is_confirmed) ||
      (filterStatus === 'pending' && !score.is_confirmed);
    
    const matchesMatchId = !filterMatchId || 
      score.match_id.toString() === filterMatchId;

    return matchesSearch && matchesStatus && matchesMatchId;
  });

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'match_id',
      headerName: 'Match ID',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          #{params.value}
        </Typography>
      ),
    },
    {
      field: 'sets',
      headerName: 'Sets',
      width: 200,
      renderCell: (params) => {
        const sets = params.value as Array<{ team1: number; team2: number }>;
        if (!sets || sets.length === 0) {
          return <Typography variant="body2" color="textSecondary">No sets</Typography>;
        }
        return (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {sets.map((set, idx) => (
              <Chip
                key={idx}
                label={`${set.team1}-${set.team2}`}
                size="small"
                variant="outlined"
                color={set.team1 > set.team2 ? 'success' : set.team2 > set.team1 ? 'error' : 'default'}
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: 'team1_score',
      headerName: 'Team 1',
      width: 100,
      renderCell: (params) => {
        const row = params.row as Score;
        return (
          <Chip
            label={params.value || 0}
            color={row.winner_team === 1 ? 'success' : 'default'}
            size="small"
          />
        );
      },
    },
    {
      field: 'team2_score',
      headerName: 'Team 2',
      width: 100,
      renderCell: (params) => {
        const row = params.row as Score;
        return (
          <Chip
            label={params.value || 0}
            color={row.winner_team === 2 ? 'success' : 'default'}
            size="small"
          />
        );
      },
    },
    {
      field: 'winner_team',
      headerName: 'Winner',
      width: 120,
      renderCell: (params) => {
        if (!params.value) return <Typography variant="body2" color="textSecondary">-</Typography>;
        return (
          <Chip
            label={`Team ${params.value}`}
            color="success"
            size="small"
            icon={<EmojiEvents />}
          />
        );
      },
    },
    {
      field: 'is_confirmed',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Confirmed' : 'Pending'}
          color={params.value ? 'success' : 'warning'}
          size="small"
          icon={params.value ? <CheckCircle /> : <Pending />}
        />
      ),
    },
    {
      field: 'submitted_at',
      headerName: 'Submitted',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value 
            ? new Date(params.value).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })
            : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Edit />}
          label="Edit"
          onClick={() => handleEditScore(params.row as AdminScore)}
        />,
        <GridActionsCellItem
          icon={<Delete />}
          label="Delete"
          onClick={() => handleDeleteScore(params.row.id)}
          showInMenu
        />,
      ],
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Score Management
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchScores}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateScore}
          >
            Add Score
          </Button>
        </Stack>
      </Stack>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2}>
              <ScoreIcon color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {stats?.total_scores || 0}
                </Typography>
                <Typography color="textSecondary" variant="body2">
                  Total Scores
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2}>
              <CheckCircle color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {stats?.confirmed_scores || 0}
                </Typography>
                <Typography color="textSecondary" variant="body2">
                  Confirmed
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Pending color="warning" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {stats?.pending_scores || 0}
                </Typography>
                <Typography color="textSecondary" variant="body2">
                  Pending
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2}>
              <TrendingUp color="info" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {stats?.average_score || 0}
                </Typography>
                <Typography color="textSecondary" variant="body2">
                  Average Score
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            placeholder="Search by Match ID or Score ID"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Match ID</InputLabel>
            <Select
              value={filterMatchId}
              label="Match ID"
              onChange={(e) => setFilterMatchId(e.target.value)}
            >
              <MenuItem value="">All Matches</MenuItem>
              {Array.from(new Set(scores.map(s => s.match_id)))
                .sort((a, b) => a - b)
                .map(matchId => (
                  <MenuItem key={matchId} value={matchId.toString()}>
                    Match #{matchId}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Scores Table */}
      <Paper>
        <DataTable
          rows={filteredScores}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={2}>
            <SportsTennis />
            <Typography variant="h6">
              {editingScore ? 'Edit Score' : 'Create New Score'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Match</InputLabel>
              <Select
                value={formData.match_id}
                label="Match"
                onChange={(e) => setFormData({ ...formData, match_id: e.target.value })}
                disabled={!!editingScore}
              >
                {matches
                  .filter(m => m.status === 'COMPLETED')
                  .map(match => (
                    <MenuItem key={match.id} value={match.id.toString()}>
                      Match #{match.id} - {new Date(match.created_at).toLocaleDateString()}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <Divider>Sets</Divider>
            <Typography variant="body2" color="text.secondary">
              Enter game scores per set (e.g. 6-4, 6-2). Set 3 appears only if the match is tied 1-1.
            </Typography>

            {syncSetRows(formData.sets).map((set, index) => (
              <Box key={index}>
                <Typography variant="subtitle2" gutterBottom>
                  Set {index + 1}
                </Typography>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Team 1"
                    type="number"
                    value={set.team1}
                    onChange={(e) => handleSetChange(index, 'team1', parseInt(e.target.value) || 0)}
                    fullWidth
                    inputProps={{ min: 0 }}
                  />
                  <Typography variant="h6" sx={{ alignSelf: 'center' }}>vs</Typography>
                  <TextField
                    label="Team 2"
                    type="number"
                    value={set.team2}
                    onChange={(e) => handleSetChange(index, 'team2', parseInt(e.target.value) || 0)}
                    fullWidth
                    inputProps={{ min: 0 }}
                  />
                </Stack>
              </Box>
            ))}

            <Divider>Summary</Divider>

            <Typography variant="body2" color="text.secondary">
              Sets won: {countSetsWon(formData.sets).team1} – {countSetsWon(formData.sets).team2}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveScore} variant="contained">
            {editingScore ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={() => {
          setOpenConfirmDialog(false);
          setDeletingScoreId(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Score"
        message="Are you sure you want to delete this score? This action cannot be undone."
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
